import React, {useEffect, useState} from 'react';
import {
  Box,
  Dialog,
  Typography,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh,
  ZoomIn,
  ZoomOut,
  Close,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

import {t} from 'i18next';
import {Document, Page, pdfjs} from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {
  ZoomButton,
  BorderButton,
  CloseButton,
  ZoomButtonsDiv
} from './ClaimViewerStyles';
import {pdfWorkerUrl} from 'src/utils/pdfWorker';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PDFPreviewModalProps {
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  printable: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  loading: boolean;
  onClose: () => void;
  buffer: ArrayBuffer | string;
  printButtonsComponent: any;
  claimType: any;
}

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  open,
  loading,
  onClose,
  buffer,
  printable,
  printButtonsComponent,
  claimType
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.5);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      setPageNumber(1);
      setScale(1.5);
    }
  }, [open]);

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const getClaimTypeLabel = () => {
    if (claimType === 'ub-04') return 'UB-04 Claim Form';
    if (claimType === 'hcfa-1500') return 'HCFA-1500 Claim Form';
    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xl">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px dotted gray'
        }}>
        <Typography sx={{fontWeight: 'bold'}}>{getClaimTypeLabel()}</Typography>
        {!loading ? (
          <ZoomButtonsDiv className="tools">
            <Box
              mr={3}
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={2}>
              <BorderButton
                title="Previous"
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}>
                <ArrowBack sx={{color: theme.palette.common.white}} />
              </BorderButton>
              <Typography sx={{fontWeight: 'bold'}}>
                Page {pageNumber} of {numPages}
              </Typography>
              <BorderButton
                title="Next"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}>
                <ArrowForward sx={{color: theme.palette.common.white}} />
              </BorderButton>
            </Box>
            <ZoomButton
              disabled={loading}
              title={t('PRIVATE.DOCUMENT.ZOOM_IN', {ns: 'private'})}
              onClick={zoomIn}>
              <ZoomIn sx={{color: theme.palette.common.white}} />
            </ZoomButton>
            <ZoomButton
              disabled={loading}
              title={t('PRIVATE.DOCUMENT.ZOOM_OUT', {ns: 'private'})}
              onClick={zoomOut}>
              <ZoomOut sx={{color: theme.palette.common.white}} />
            </ZoomButton>
            <ZoomButton
              disabled={loading}
              title={t('PRIVATE.DOCUMENT.RESET', {ns: 'private'})}
              onClick={resetZoom}>
              <Refresh sx={{color: theme.palette.common.white}} />
            </ZoomButton>
            {printable ? (
              <>
                <Box ml={3} />
                {printButtonsComponent(claimType)}
              </>
            ) : null}
            <Box ml={3} />
            <CloseButton title="Close" onClick={onClose}>
              <Close sx={{color: theme.palette.common.black}} />
            </CloseButton>
          </ZoomButtonsDiv>
        ) : null}
      </DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            backgroundColor: 'rgb(244 246 248)',
            minHeight: '90vh',
            height: '100%'
          }}>
          {loading ? (
            <ComponentLoader />
          ) : (
            <Document
              file={buffer}
              onLoadSuccess={pdf => setNumPages(pdf.numPages)}
              loading={<Typography>Loading PDF...</Typography>}>
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PDFPreviewModal;
